import { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * A type-to-filter multi-select — the pattern MUI calls Autocomplete.
 *
 * Selected values render as removable chips inside the field; typing filters the
 * list; the list is a real listbox with keyboard navigation. Controlled: it owns
 * no selection of its own, so the URL stays the source of truth for callers like
 * the notification filters.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
 * The notification centre has twenty-one event types. Rendering them as
 * twenty-one chips took more vertical space than the history underneath, so they
 * were hidden behind a disclosure — which trades one problem for a worse one:
 * you cannot filter by a type without first remembering it exists, then opening
 * a drawer and reading all twenty-one to find it. Typing three letters is the
 * interaction that list actually wants.
 *
 * ── MULTI-SELECT ONLY, DELIBERATELY ──────────────────────────────────────────
 * There is no `multiple={false}` mode. Single-select would need its own display
 * branch (the chosen label sits IN the input rather than beside it), its own
 * clear affordance, and its own idea of what Enter does — and nothing in this
 * app wants one; `Select` already covers the short fixed lists. Adding it later
 * is a contained change, but shipping it now would mean shipping a code path
 * with no caller and no test.
 *
 * ── MATCHING IS ON THE LABEL, NOT THE VALUE ──────────────────────────────────
 * Values here are stable identifiers like `WINNER_DETECTED`; labels are what the
 * catalogue shows a human. Someone typing "winner" means the label, and matching
 * the value as well would make the list jump on substrings nobody typed on
 * purpose. `description` is searched too, because for these events it is the
 * sentence that says when the thing fires and is often what someone remembers
 * instead of the name.
 *
 * @param {Object} props
 * @param {string} [props.label]        Field label.
 * @param {Array}  props.options        [{ value, label, description }] or plain strings.
 * @param {string[]} props.value        Selected values.
 * @param {(next: string[]) => void} props.onChange  Given the WHOLE next selection.
 * @param {string} [props.placeholder]  Shown when nothing is selected and nothing typed.
 * @param {string} [props.emptyText]    Shown when the filter matches nothing.
 */
/*
 * The three icons, inline.
 *
 * Drawn as SVG paths rather than typed as ✕ / ▾ characters: a glyph renders at
 * whatever weight and baseline the user's font decides, which is why the first
 * pass looked spindly and sat a pixel high inside its chip. These are the same
 * shapes Material uses — a filled cancel disc, a stroke-weight clear cross, and
 * a solid caret — and they scale with `currentColor` and font-size.
 */
function CancelIcon() {
  return (
    <svg className='ac__icon' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
      <path d='M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z' />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg className='ac__icon' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
      <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className='ac__icon' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
      <path d='M7 10l5 5 5-5z' />
    </svg>
  );
}

export default function Autocomplete({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'Type to search…',
  emptyText = 'No matches',
  disabled = false,
  wrapperClassName = '',
  className = '',
  id,
}) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const listId = `${fieldId}-list`;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const tagsRef = useRef(null);

  // Plain strings are accepted so a caller with a list of names does not have to
  // map it into objects first.
  const items = useMemo(() => (options ?? []).map(o => (
    typeof o === 'string'
      ? { value: o, label: o, description: '' }
      : { value: o.value, label: o.label ?? String(o.value), description: o.description ?? '' }
  )), [options]);

  const selected = useMemo(() => new Set(value ?? []), [value]);

  /**
   * The chips, in `value` order.
   *
   * Derived from `value` rather than by filtering `options`, for two reasons.
   * A selected value with no matching option — a stale bookmark naming a type
   * that has since been retired — still gets a chip, so it is visible and
   * removable instead of silently filtering the list from nowhere. And the chips
   * then sit in the same order as `value`, which is what lets Backspace remove
   * the last one you can actually SEE rather than the last one in an array whose
   * order the user has no view of.
   */
  const chips = useMemo(() => (value ?? []).map(
    v => items.find(i => i.value === v) ?? { value: v, label: String(v), description: '' },
  ), [value, items]);

  /**
   * Filtered, with LABEL matches ranked above description-only matches.
   *
   * The ranking is not a nicety. Searching the description as well is what makes
   * "the one about an hour with no spend" findable — but several descriptions
   * mention words that are another event's NAME, and a flat filter then puts the
   * wrong row first. Typing "spend" matched `WINNER_DETECTED` ahead of
   * `NO_SPEND`, because the winner description ends "...takes another $1,000 in
   * spend". Since Enter takes the first row, that is not a cosmetic ordering
   * problem: it selects the wrong filter for anyone who types and commits.
   *
   * Ties keep catalogue order, which is the order the preferences screen and the
   * whole feature already present these in.
   */
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    const byLabel = [];
    const byDescription = [];
    for (const i of items) {
      if (i.label.toLowerCase().includes(needle)) byLabel.push(i);
      else if (i.description.toLowerCase().includes(needle)) byDescription.push(i);
    }
    return [...byLabel, ...byDescription];
  }, [items, query]);

  // Typing aims at the first match, so Enter after three letters picks the
  // obvious thing. Opening the list without typing highlights nothing, so Enter
  // cannot select something the user never looked at.
  useEffect(() => {
    setHighlight(query.trim() ? 0 : -1);
  }, [query]);

  // Keep the highlighted option in view when arrowing past the fold. `nearest`
  // rather than `center` so the list does not lurch on every keypress.
  useEffect(() => {
    if (highlight < 0) return;
    listRef.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  // Close on an outside press. mousedown rather than click: a click fires after
  // the press has already moved focus, which makes the panel flicker shut on
  // presses that started inside it.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commit = (next) => onChange?.(next);

  const toggle = (item) => {
    const next = selected.has(item.value)
      ? (value ?? []).filter(v => v !== item.value)
      : [...(value ?? []), item.value];
    commit(next);
    // The query is cleared but the list stays open, so picking three types in a
    // row is three keystroke-and-Enter cycles rather than three round trips
    // through the field.
    setQuery('');
    inputRef.current?.focus();
  };

  // Drops the rightmost chip. Safe as a plain slice because `chips` is built
  // from `value` in order, so the last entry is the one on screen.
  const removeLast = () => {
    if (!value?.length) return;
    commit(value.slice(0, -1));
  };

  const onKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight(h => (matches.length ? (h + 1) % matches.length : -1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight(h => (matches.length ? (h <= 0 ? matches.length - 1 : h - 1) : -1));
      return;
    }
    if (e.key === 'Enter') {
      if (!open || highlight < 0 || !matches[highlight]) return;
      // Only swallowed when it is actually selecting something, so Enter still
      // submits a surrounding form when the list is closed.
      e.preventDefault();
      toggle(matches[highlight]);
      return;
    }
    if (e.key === 'Escape') {
      if (!open) return;
      e.preventDefault();
      setOpen(false);
      return;
    }
    // Backspace on an empty box peels the last chip off — the behaviour every
    // tag field has, and the only way to undo a selection without reaching for
    // the mouse.
    if (e.key === 'Backspace' && !query) removeLast();
  };

  const control = (
    <div
      className={['ac', disabled ? 'ac--disabled' : '', className].filter(Boolean).join(' ')}
      ref={rootRef}
    >
      {/* Clicking anywhere in the box focuses the input, the way a native field
          with padding behaves. Presentation role because the real control is the
          input inside it — this div must not become a tab stop of its own. */}
      <div
        className='ac__field'
        role='presentation'
        onMouseDown={(e) => {
          // Only when the press landed on the padding — otherwise a chip's own
          // button, or an adornment, would lose the press to this handler.
          if (e.target === e.currentTarget || e.target === tagsRef.current) {
            e.preventDefault();
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        {/* Chips and the input wrap together; the adornments below sit outside
            this box so they stay pinned right instead of wrapping into the
            middle of a second row of chips. */}
        <div className='ac__tags' ref={tagsRef}>
          {chips.map(i => (
            <span className='ac__chip' key={i.value}>
              <span className='ac__chip-label'>{i.label}</span>
              <button
                type='button'
                className='ac__chip-x'
                disabled={disabled}
                aria-label={`Remove ${i.label}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => toggle(i)}
              >
                <CancelIcon />
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            id={fieldId}
            className='ac__input'
            type='text'
            autoComplete='off'
            disabled={disabled}
            value={query}
            placeholder={value?.length ? '' : placeholder}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            role='combobox'
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete='list'
            aria-activedescendant={open && highlight >= 0 ? `${listId}-${highlight}` : undefined}
          />
        </div>

        <div className='ac__adorn'>
          {/* Clear-all only exists when it has something to do. A permanently
              visible one is a button that does nothing most of the time, and it
              reads as enabled. */}
          {(chips.length > 0 || query) && !disabled && (
            <button
              type='button'
              className='ac__btn'
              aria-label='Clear all'
              title='Clear'
              onMouseDown={e => e.preventDefault()}
              onClick={() => { commit([]); setQuery(''); inputRef.current?.focus(); }}
            >
              <ClearIcon />
            </button>
          )}
          <button
            type='button'
            className={`ac__btn ac__btn--arrow${open ? ' ac__btn--open' : ''}`}
            // The listbox has its own control — the input — so this is decoration
            // for the pointer. Hidden from the tree rather than given a second,
            // conflicting aria-expanded on the same popup.
            aria-hidden='true'
            tabIndex={-1}
            disabled={disabled}
            onMouseDown={e => e.preventDefault()}
            onClick={() => { setOpen(o => !o); inputRef.current?.focus(); }}
          >
            <ArrowIcon />
          </button>
        </div>
      </div>

      {open && (
        <div className='ac__panel'>
          {matches.length === 0 ? (
            <div className='ac__empty'>{emptyText}</div>
          ) : (
            <ul className='ac__list' id={listId} role='listbox' aria-multiselectable='true' ref={listRef}>
              {matches.map((item, i) => {
                const on = selected.has(item.value);
                return (
                  /* No key handler on the option, and that is correct rather
                     than an oversight: focus never leaves the input for the
                     whole interaction, so every key is handled there and the
                     options are only ever pointer targets. That is the combobox
                     pattern, and aria-activedescendant is what tells a screen
                     reader which one is current. */
                  <li
                    key={item.value}
                    id={`${listId}-${i}`}
                    role='option'
                    aria-selected={on}
                    className={[
                      'ac__option',
                      on ? 'ac__option--on' : '',
                      i === highlight ? 'ac__option--active' : '',
                    ].filter(Boolean).join(' ')}
                    // mousedown, not click: click lands after the input has
                    // already blurred, which closes the panel first.
                    onMouseDown={(e) => { e.preventDefault(); toggle(item); }}
                    onMouseEnter={() => setHighlight(i)}
                  >
                    <span className='ac__option-body'>
                      <span className='ac__option-label'>{item.label}</span>
                      {item.description && (
                        <span className='ac__option-desc'>{item.description}</span>
                      )}
                    </span>
                    {/* After the body, not before it. A leading tick column
                        indents every label by the width of a mark that most
                        rows do not have, so the list reads as though it starts
                        somewhere to the right of its own panel. */}
                    <span className='ac__tick' aria-hidden='true'>{on ? '✓' : ''}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  if (!label) return control;

  return (
    <div className={['form-group', wrapperClassName].filter(Boolean).join(' ')}>
      <label htmlFor={fieldId}>{label}</label>
      {control}
    </div>
  );
}
